import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { 
  ArrowLeft, 
  Plus, 
  Pencil, 
  Trash2, 
  Search, 
  BookMarked,
  ExternalLink,
  Loader2,
  BookOpen,
  Upload,
  X,
  Image as ImageIcon
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function AdminStudyGuides() {
  const navigate = useNavigate();
  const [guides, setGuides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [pagination, setPagination] = useState({ page: 1, total: 0, total_pages: 0 });
  
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedGuide, setSelectedGuide] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  
  const imageInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    amazon_url: '',
    cover_image_url: '',
    category: '',
    status: 'draft'
  });

  const fetchGuides = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: pagination.page,
        page_size: 50
      });
      if (search) params.append('search', search);
      if (statusFilter !== 'all') params.append('status', statusFilter);
      
      const res = await fetch(`${API}/api/admin/study-guides?${params}`, { credentials: 'include' });
      if (!res.ok) throw new Error('Failed to fetch guides');
      const data = await res.json();
      setGuides(data.guides || []);
      setPagination(data.pagination || { page: 1, total: 0, total_pages: 0 });
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter, pagination.page]);

  useEffect(() => {
    fetchGuides();
  }, [fetchGuides]);

  const openCreateDialog = () => {
    setSelectedGuide(null);
    setFormData({
      title: '',
      description: '',
      amazon_url: '',
      cover_image_url: '',
      category: '',
      status: 'draft'
    });
    setShowEditDialog(true);
  };

  const openEditDialog = async (guide) => {
    try {
      const res = await fetch(`${API}/api/admin/study-guides/${guide.guide_id}`, { credentials: 'include' });
      if (!res.ok) throw new Error('Failed to fetch guide');
      const data = await res.json();
      
      setSelectedGuide(data);
      setFormData({
        title: data.title || '',
        description: data.description || '',
        amazon_url: data.amazon_url || '',
        cover_image_url: data.cover_image_url || '',
        category: data.category || '',
        status: data.status || 'draft'
      });
      setShowEditDialog(true);
    } catch (err) {
      toast.error(err.message);
    }
  };

  // Cover image upload handler
  const handleCoverUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Validate file type
    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type. Allowed: PNG, JPG, WEBP');
      return;
    }
    
    // Validate file size (10MB max)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 10MB');
      return;
    }
    
    setUploading(true);
    try {
      const uploadData = new FormData();
      uploadData.append('file', file);
      
      const res = await fetch(`${API}/api/admin/study-guides/upload-cover`, {
        method: 'POST',
        credentials: 'include',
        body: uploadData
      });
      
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Upload failed');
      }
      
      const data = await res.json();
      setFormData(prev => ({
        ...prev,
        cover_image_url: `${API}${data.url}`
      }));
      toast.success('Cover image uploaded');
    } catch (err) {
      toast.error(`Failed to upload: ${err.message}`);
    } finally {
      setUploading(false);
      if (imageInputRef.current) imageInputRef.current.value = '';
    }
  };

  const removeCoverImage = () => {
    setFormData(prev => ({ ...prev, cover_image_url: '' }));
  };

  const handleSave = async () => {
    if (!formData.title.trim()) {
      toast.error('Title is required');
      return;
    }
    if (!formData.amazon_url.trim()) {
      toast.error('Amazon URL is required');
      return;
    }
    
    // Basic URL validation
    try {
      new URL(formData.amazon_url);
    } catch {
      toast.error('Please enter a valid Amazon URL');
      return;
    }
    
    setSaving(true);
    try {
      const url = selectedGuide
        ? `${API}/api/admin/study-guides/${selectedGuide.guide_id}`
        : `${API}/api/admin/study-guides`;
      
      const res = await fetch(url, {
        method: selectedGuide ? 'PUT' : 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          cover_image_url: formData.cover_image_url || null,
          category: formData.category || null
        })
      });
      
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Failed to save');
      }
      
      toast.success(selectedGuide ? 'Study guide updated' : 'Study guide created');
      setShowEditDialog(false);
      fetchGuides();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedGuide) return;
    
    try {
      const res = await fetch(`${API}/api/admin/study-guides/${selectedGuide.guide_id}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      if (!res.ok) throw new Error('Failed to delete');
      
      toast.success('Study guide deleted');
      setShowDeleteDialog(false);
      setSelectedGuide(null);
      fetchGuides();
    } catch (err) {
      toast.error(err.message);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <Button
            variant="ghost"
            onClick={() => navigate('/admin')}
            className="text-slate-600 hover:text-slate-900 mb-4"
            data-testid="back-to-admin"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Admin
          </Button>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-amber-100 rounded-lg">
                <BookMarked className="w-8 h-8 text-amber-600" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-slate-900">Shop</h1>
                <p className="text-slate-500">Manage Amazon product links for study guides</p>
              </div>
            </div>
            <Button
              onClick={openCreateDialog}
              className="bg-amber-500 hover:bg-amber-600 text-white"
              data-testid="create-guide-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Product
            </Button>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="max-w-6xl mx-auto px-4 py-4">
        <div className="flex gap-4 flex-wrap">
          <div className="relative flex-1 min-w-[200px] max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search guides..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 bg-white border-slate-200"
              data-testid="search-input"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-36 bg-white border-slate-200">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="draft">Draft</SelectItem>
              <SelectItem value="published">Published</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-6xl mx-auto px-4 py-4">
        {loading ? (
          <div className="text-center py-12">
            <Loader2 className="w-8 h-8 animate-spin mx-auto text-slate-400 mb-2" />
            <p className="text-slate-500">Loading...</p>
          </div>
        ) : guides.length === 0 ? (
          <Card className="bg-white border-slate-200">
            <CardContent className="py-12 text-center">
              <BookOpen className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-700 mb-2">No Products</h3>
              <p className="text-slate-500 mb-4">
                {search || statusFilter !== 'all'
                  ? 'No products match your filters.'
                  : 'Add your first product with an Amazon link.'}
              </p>
              {!search && statusFilter === 'all' && (
                <Button onClick={openCreateDialog} className="bg-amber-500 hover:bg-amber-600 text-white">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Product
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {guides.map((guide) => (
              <Card
                key={guide.guide_id}
                className="bg-white border-slate-200 hover:shadow-md transition-shadow"
                data-testid={`guide-card-${guide.guide_id}`}
              >
                <CardContent className="p-4">
                  {/* Thumbnail */}
                  {guide.cover_image_url ? (
                    <div className="aspect-[3/2] bg-slate-100 rounded-lg mb-3 overflow-hidden">
                      <img
                        src={guide.cover_image_url}
                        alt={guide.title}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  ) : (
                    <div className="aspect-[3/2] bg-gradient-to-br from-amber-50 to-orange-100 rounded-lg mb-3 flex items-center justify-center">
                      <BookOpen className="w-10 h-10 text-amber-300" />
                    </div>
                  )}
                  
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <h3 className="font-semibold text-slate-900 line-clamp-1">{guide.title}</h3>
                    <Badge 
                      variant={guide.status === 'published' ? 'default' : 'secondary'}
                      className={guide.status === 'published' 
                        ? 'bg-green-100 text-green-700 border-green-200' 
                        : 'bg-amber-100 text-amber-700 border-amber-200'}
                    >
                      {guide.status}
                    </Badge>
                  </div>
                  
                  {guide.description && (
                    <p className="text-sm text-slate-500 line-clamp-2 mb-2">{guide.description}</p>
                  )}
                  
                  {guide.category && (
                    <Badge variant="outline" className="bg-slate-50 text-slate-600 border-slate-200 text-xs mb-3">
                      {guide.category}
                    </Badge>
                  )}
                  
                  <div className="flex items-center justify-between pt-3 border-t border-slate-100">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => window.open(guide.amazon_url, '_blank', 'noopener,noreferrer')}
                      className="text-amber-600 hover:text-amber-700"
                    >
                      <ExternalLink className="w-4 h-4 mr-1" />
                      Amazon
                    </Button>
                    <div className="flex items-center gap-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => openEditDialog(guide)}
                        className="text-slate-500 hover:text-teal-600"
                        data-testid={`edit-guide-${guide.guide_id}`}
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          setSelectedGuide(guide);
                          setShowDeleteDialog(true);
                        }}
                        className="text-slate-500 hover:text-red-600"
                        data-testid={`delete-guide-${guide.guide_id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Pagination */}
        {pagination.total_pages > 1 && (
          <div className="flex justify-center gap-2 pt-6">
            <Button
              variant="outline"
              size="sm"
              disabled={pagination.page === 1}
              onClick={() => setPagination(p => ({ ...p, page: p.page - 1 }))}
              className="border-slate-200"
            >
              Previous
            </Button>
            <span className="px-4 py-2 text-slate-500">
              Page {pagination.page} of {pagination.total_pages}
            </span>
            <Button
              variant="outline"
              size="sm"
              disabled={pagination.page === pagination.total_pages}
              onClick={() => setPagination(p => ({ ...p, page: p.page + 1 }))}
              className="border-slate-200"
            >
              Next
            </Button>
          </div>
        )}
      </div>

      {/* Edit/Create Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto bg-white">
          <DialogHeader>
            <DialogTitle className="text-slate-900">
              {selectedGuide ? 'Edit Study Guide' : 'Add Study Guide'}
            </DialogTitle>
            <DialogDescription className="text-slate-500">
              Enter the details, Amazon link, and cover image for this study guide.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Cover Image Upload */}
            <div>
              <Label className="text-slate-700">Cover Image</Label>
              <div className="mt-1 border-2 border-dashed border-slate-200 rounded-lg p-4">
                {formData.cover_image_url ? (
                  <div className="relative">
                    <img
                      src={formData.cover_image_url}
                      alt="Cover preview"
                      className="w-full aspect-[3/4] object-cover rounded-lg"
                    />
                    <button
                      type="button"
                      onClick={removeCoverImage}
                      className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1.5 shadow-md hover:bg-red-600"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <>
                    <input
                      ref={imageInputRef}
                      type="file"
                      accept="image/png,image/jpeg,image/jpg,image/webp"
                      onChange={handleCoverUpload}
                      className="hidden"
                      id="cover-upload"
                      data-testid="cover-upload-input"
                    />
                    <label 
                      htmlFor="cover-upload" 
                      className={`flex flex-col items-center cursor-pointer py-6 ${uploading ? 'pointer-events-none' : ''}`}
                    >
                      {uploading ? (
                        <>
                          <Loader2 className="w-8 h-8 text-amber-500 animate-spin mb-2" />
                          <span className="text-sm text-slate-600">Uploading...</span>
                        </>
                      ) : (
                        <>
                          <Upload className="w-8 h-8 text-slate-400 mb-2" />
                          <span className="text-sm text-slate-600">Click to upload cover image</span>
                          <span className="text-xs text-slate-400 mt-1">PNG, JPG, WEBP (max 10MB)</span>
                        </>
                      )}
                    </label>
                  </>
                )}
              </div>
            </div>
            
            <div>
              <Label className="text-slate-700">Title *</Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData(p => ({ ...p, title: e.target.value }))}
                placeholder="e.g., Critical Care Nursing Review"
                className="mt-1 bg-white border-slate-200"
                data-testid="guide-title-input"
              />
            </div>
            
            <div>
              <Label className="text-slate-700">Description</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData(p => ({ ...p, description: e.target.value }))}
                placeholder="Brief description of this guide..."
                className="mt-1 min-h-[60px] bg-white border-slate-200"
              />
            </div>
            
            <div>
              <Label className="text-slate-700">Amazon URL *</Label>
              <Input
                value={formData.amazon_url}
                onChange={(e) => setFormData(p => ({ ...p, amazon_url: e.target.value }))}
                placeholder="https://www.amazon.com/..."
                className="mt-1 bg-white border-slate-200"
                data-testid="guide-amazon-url-input"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-700">Category (optional)</Label>
                <Input
                  value={formData.category}
                  onChange={(e) => setFormData(p => ({ ...p, category: e.target.value }))}
                  placeholder="e.g., ICU, Pharmacology"
                  className="mt-1 bg-white border-slate-200"
                />
              </div>
              <div>
                <Label className="text-slate-700">Status</Label>
                <Select
                  value={formData.status}
                  onValueChange={(v) => setFormData(p => ({ ...p, status: v }))}
                >
                  <SelectTrigger className="mt-1 bg-white border-slate-200">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="draft">Draft</SelectItem>
                    <SelectItem value="published">Published</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setShowEditDialog(false)}
              className="border-slate-200"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving || uploading}
              className="bg-amber-500 hover:bg-amber-600 text-white"
              data-testid="save-guide-btn"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                selectedGuide ? 'Update' : 'Create'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-slate-900">Delete Study Guide?</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-600">
              This will permanently delete "{selectedGuide?.title}". This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-slate-200">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="confirm-delete-btn"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
